import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiMonitor } from 'react-icons/fi';

import { useStudentLoginMutation } from '../../Shared/API/AssessmentsApi';
import { useAuth } from '../../Shared/Hooks/useAuth';
import QRScanner from '../../Shared/Components/QRScanner';
import {
    Button,
    Input,
    Surface,
    VStack,
    HStack,
} from '../../Shared/Components/ui';
import Loader from '../../Shared/Components/Loader';

const StudentLogin = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const returnUrl = searchParams.get('returnUrl');

    const [showScanner, setShowScanner] = useState(false);
    const { authenticate } = useAuth();
    const [studentLogin, { isLoading: isLoggingIn }] = useStudentLoginMutation();

    const handleLogin = async (code: string) => {
        try {
            const deviceId = window.navigator.userAgent;

            const result = await studentLogin({
                code,
                deviceId,
                geoLocation: null
            }).unwrap();

            authenticate(result as any);
            toast.success(`Welcome ${result.student?.displayName || 'Student'}!`);

            if (returnUrl) {
                navigate(returnUrl);
            } else {
                navigate('/dashboard');
            }
        } catch (err) {
            toast.error('Invalid Student ID or unable to login.');
        }
    };

    return (
        <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6">
            <Surface elevation={2} className="w-full max-w-md p-8 border border-slate-800/50 bg-slate-900/50 backdrop-blur-xl">
                <VStack gap={8} align="stretch">
                    <VStack align="center" gap={4}>
                        <div className="w-16 h-16 bg-teal-500/10 rounded-2xl flex items-center justify-center border border-teal-500/20 shadow-[0_0_20px_rgba(20,184,166,0.1)]">
                            <FiMonitor className="text-teal-400 text-3xl" />
                        </div>
                        <VStack align="center" gap={1}>
                            <h1 className="text-2xl font-bold text-white">Student Portal</h1>
                            <p className="text-slate-400 text-center text-sm px-4">
                                Please scan your QR code or enter your ID to access your assessments.
                            </p>
                        </VStack>
                    </VStack>

                    {!showScanner ? (
                        <VStack gap={6} align="stretch">
                            <Button
                                variant="primary"
                                className="h-14 text-lg font-bold shadow-lg shadow-teal-500/20 w-full"
                                onClick={() => setShowScanner(true)}
                            >
                                Scan Student ID
                            </Button>

                            <HStack align="center" gap={4} className="opacity-50">
                                <div className="flex-1 h-px bg-slate-800" />
                                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Or Manual Entry</span>
                                <div className="flex-1 h-px bg-slate-800" />
                            </HStack>

                            <form onSubmit={(e) => {
                                e.preventDefault();
                                const formData = new FormData(e.currentTarget);
                                const code = formData.get('code') as string;
                                if (code) handleLogin(code);
                            }}>
                                <Input
                                    name="code"
                                    type="text"
                                    placeholder="Enter Student Number"
                                    autoComplete="off"
                                    className="bg-slate-950/50 border-slate-800 focus:border-teal-500 text-white h-12 mb-4"
                                    required
                                />
                                <Button
                                    type="submit"
                                    variant="outline"
                                    className="h-12 border-slate-800 hover:bg-slate-800 text-slate-300 w-full"
                                    disabled={isLoggingIn}
                                >
                                    {isLoggingIn ? 'Verifying...' : 'Login with ID'}
                                </Button>
                            </form>
                        </VStack>
                    ) : (
                        <VStack align="center" gap={6}>
                            <div className="relative rounded-3xl overflow-hidden border-2 border-teal-500/50 shadow-[0_0_40px_rgba(20,184,166,0.2)] bg-black">
                                <QRScanner
                                    onResult={(code) => handleLogin(code)}
                                    stopAfterScan={true}
                                />
                                {isLoggingIn && (
                                    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center">
                                        <Loader color="fill-teal-500" />
                                    </div>
                                )}
                            </div>
                            <Button
                                variant="ghost"
                                onClick={() => setShowScanner(false)}
                                className="text-slate-400 hover:text-white"
                                startIcon={<FiArrowLeft />}
                            >
                                Back to options
                            </Button>
                        </VStack>
                    )}

                    <div className="pt-4 text-center">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/login')}
                            className="text-slate-500 hover:text-teal-400"
                        >
                            Are you a Staff member? Login here
                        </Button>
                    </div>
                </VStack>
            </Surface>
        </div>
    );
};

export default StudentLogin;
